import React from 'react';
import { cn } from '../../../utils/cn';
import {
  ALL_CONTACTS,
  getCenterContactForTooth,
  getIprContactValue,
  getSelectionToothForContact,
  IPR_TOOL_SELECT,
  isIprContactDisabled,
} from '../../../lib/iprToothData';

const IPRInstructionsTable = ({
  iprData,
  onIPRUpdate,
  selectedTooth,
  onToothSelect,
  activeTool = IPR_TOOL_SELECT,
  readOnly = false,
}) => {
  const selectedContact = getCenterContactForTooth(selectedTooth);
  const selectedDisabled = selectedContact
    ? isIprContactDisabled(iprData, selectedContact)
    : false;
  const activeContacts = ALL_CONTACTS.filter(
    (contact) => getIprContactValue(iprData, contact) > 0
  );

  const ContactSummary = ({ contacts, title }) => (
    <div>
      <h4 className="mb-3 text-sm font-semibold text-gray-700">{title}</h4>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white/80">
        {contacts.length === 0 ? (
          <p className="px-4 py-5 text-center text-sm text-gray-500">
            Aucun contact IPR saisi.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200/80">
            {contacts.map((contact) => {
              const value = getIprContactValue(iprData, contact);
              const selected = selectedContact?.id === contact.id;
              const content = (
                <>
                  <span className="font-semibold text-gray-900">
                    Contact {contact.label}
                  </span>
                  <span className="font-bold text-red-600">{value.toFixed(2)} mm</span>
                </>
              );

              return (
                <li key={contact.id}>
                  {readOnly ? (
                    <div className="flex min-h-[44px] items-center justify-between gap-3 px-4 py-2.5">
                      {content}
                    </div>
                  ) : (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        onToothSelect?.(getSelectionToothForContact(contact))
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onToothSelect?.(getSelectionToothForContact(contact));
                        }
                      }}
                      className={cn(
                        'flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors',
                        'hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
                        selected && 'bg-primary/10'
                      )}
                      aria-label={`Modifier le contact ${contact.label}, ${value.toFixed(2)} millimètres`}
                    >
                      {content}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  const upperContacts = activeContacts.filter((contact) => contact.arch === 'upper');
  const lowerContacts = activeContacts.filter((contact) => contact.arch === 'lower');

  return (
    <div className="border-b border-gray-200/80 p-6 md:p-8">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">Contacts IPR (mm)</h3>

      {!readOnly && activeTool === IPR_TOOL_SELECT && (
        <div className="ipr-subtle-chip ipr-pdf-hide print:hidden mb-6 p-4">
          {selectedContact ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <p className="text-base font-semibold text-gray-900">
                  Contact {selectedContact.label}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  Le côté réellement réduit se choisit au fauteuil, hors application.
                </p>
              </div>
              <div className="w-full sm:w-56">
                <label
                  htmlFor="ipr-contact-value"
                  className="mb-1.5 block text-xs font-semibold text-gray-700"
                >
                  Réduction interdentaire (mm)
                </label>
                {selectedDisabled ? (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    Contact indisponible — dent manquante (M).
                  </div>
                ) : (
                  <input
                    id="ipr-contact-value"
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    value={getIprContactValue(iprData, selectedContact) || ''}
                    onChange={(event) =>
                      onIPRUpdate?.(selectedContact.id, event.target.value)
                    }
                    className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-base font-semibold text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="0.00"
                    aria-describedby="ipr-contact-help"
                  />
                )}
                <p id="ipr-contact-help" className="mt-1 text-xs text-gray-500">
                  Une valeur par espace, de 0,05 à 1,00 mm.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-center text-sm text-gray-600">
              Sélectionnez une dent sur le schéma pour renseigner le contact vers le centre de l’arcade.
            </p>
          )}
        </div>
      )}

      {activeContacts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
          <p className="font-medium text-gray-700">Aucun contact IPR saisi.</p>
          {!readOnly && (
            <p className="mt-1 text-sm text-gray-500">
              Cliquez sur une dent, puis indiquez la mesure du contact.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ContactSummary contacts={upperContacts} title="Arcade supérieure" />
          <ContactSummary contacts={lowerContacts} title="Arcade inférieure" />
        </div>
      )}
    </div>
  );
};

export default IPRInstructionsTable;
